/**
 * Work, rendered as work (M11, PRD §5.3, FR-3.5). The doorman does not spend in
 * silence and it does not spend in dollars: a piece of work announces itself,
 * shows its progress, and settles into a line of what it found — and not one of
 * these carries a money figure.
 *
 * There is no number in this file at all (owner decision 2026-08-03). The
 * sender agreed the drawdown when they bought the plan, and what a piece of
 * work costs is not a thing the hearing tells them — the plan screen carries
 * the share of the period it all came out of, afterwards.
 *
 * Free of network state, like everything below `KnockThread`: what a work item
 * reads as is a function of its own fields, so every state — working, part-way,
 * done, failed — is rendered and asserted on directly.
 */
import type { WorkOutcome } from "../../lib/sse";

/**
 * A settle mark, not an emoji. `✓` (U+2713) is a check from the Dingbats block
 * with no emoji presentation — the same glyph the receipt uses for a line that
 * is paid for.
 */
export const CHECK = "✓";

/** One piece of work as the thread has watched it happen. */
export type WorkItemState = {
  id: string;
  kind: string;
  /** The doorman's sentence for it — "Reading your deck", then "Deck reviewed". */
  label: string;
  /** What the work is measured in; `0` when the count is not known yet. */
  units: number;
  /** `null` until the first progress lands; a fraction 0..1 after. */
  progress: number | null;
  done: boolean;
  units_executed: number;
  outcome: WorkOutcome;
};

/**
 * The working sentence. The count is derived from the fraction and the unit
 * total, and shown only when the total is known — a bar with no scale says
 * nothing, so it says nothing rather than a wrong thing.
 */
export function progressLine(item: WorkItemState): string {
  if (item.progress !== null && item.units > 0) {
    return `${item.label} — ${Math.round(item.progress * item.units)} of ${item.units}`;
  }
  return item.label;
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? "" : "s"}`;
}

/**
 * A terse line of what the work turned up, from the products it reports — and
 * never money. A piece of work that failed found nothing worth a line.
 */
export function outcomeSummary(outcome: WorkOutcome): string {
  if (outcome["failed"]) return "";
  const n = (key: string): number =>
    typeof outcome[key] === "number" ? (outcome[key] as number) : 0;
  if ("claims_found" in outcome) return plural(n("claims_found"), "claim") + " found";
  if ("verified" in outcome) return `${n("verified")} of ${claimsChecked(outcome)} verified`;
  if ("sources_checked" in outcome) return plural(n("sources_checked"), "source");
  if ("pages_reviewed" in outcome) return plural(n("pages_reviewed"), "page") + " read";
  return "";
}

/**
 * How many claims a verification checked. The server's words are `verified`,
 * `contradicted` and `unverified` (`knocks/work.ts`); this counted `refuted`
 * and `inconclusive`, which it never sends, so two unverified claims read as
 * "0 of 0 verified" under a check mark (`md-device`, first UI review).
 */
function claimsChecked(outcome: WorkOutcome): number {
  const n = (key: string): number =>
    typeof outcome[key] === "number" ? (outcome[key] as number) : 0;
  return n("verified") + n("contradicted") + n("unverified");
}

/** What a failed check says — the server's own sentence to the doorman, said to the knocker. */
export const WORK_FAILED_LINE = "That check could not be completed.";

/**
 * A SETTLED LINE EARNS ITS CHECK MARK (first UI review, `md-device`). "✓ Claims
 * verified · 0 of 0 verified" told a knocker their claims were verified when
 * none were. The mark is for work that found something: a verification that
 * verified at least one claim, a reading that read a page, a search that
 * checked a source. Everything else says what happened, without the mark — and
 * a verification that verified nothing does not wear the word "verified" as its
 * headline.
 */
export function settledLine(item: WorkItemState): {
  mark: boolean;
  label: string;
  summary: string;
} {
  const { outcome } = item;
  if (outcome["failed"]) return { mark: false, label: WORK_FAILED_LINE, summary: "" };
  const n = (key: string): number =>
    typeof outcome[key] === "number" ? (outcome[key] as number) : 0;
  const summary = outcomeSummary(outcome);
  if ("verified" in outcome) {
    const checked = claimsChecked(outcome);
    if (checked === 0) return { mark: false, label: "No claims checked", summary: "" };
    if (n("verified") === 0) return { mark: false, label: "Claims checked", summary };
    return { mark: true, label: item.label, summary };
  }
  if ("sources_checked" in outcome && n("sources_checked") === 0) {
    return { mark: false, label: "No sources checked", summary: "" };
  }
  if ("pages_reviewed" in outcome && n("pages_reviewed") === 0) {
    return { mark: false, label: "No pages read", summary: "" };
  }
  return { mark: true, label: item.label, summary };
}

/**
 * The active and completed work, stacked below the doorman's current line. A
 * quiet card: an amber dot that breathes while work is in flight, a settled
 * zinc line once it is done.
 */
export function WorkIndicator({ items }: { items: WorkItemState[] }) {
  if (items.length === 0) return null;
  return (
    <div className="space-y-1.5" aria-live="polite">
      {items.map((item) =>
        item.done ? <Settled key={item.id} item={item} /> : <Working key={item.id} item={item} />,
      )}
    </div>
  );
}

function Working({ item }: { item: WorkItemState }) {
  return (
    <p className="flex items-center justify-center gap-2 text-center text-xs text-faint">
      <span
        className="nob-breathe inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-accent"
        aria-hidden="true"
      />
      {progressLine(item)}
    </p>
  );
}

function Settled({ item }: { item: WorkItemState }) {
  const { mark, label, summary } = settledLine(item);
  return (
    <p className="flex items-center justify-center gap-2 text-center text-xs text-faint">
      {mark && (
        <span
          className="inline-block w-1.5 shrink-0 text-center text-accent-ink/70"
          aria-hidden="true"
        >
          {CHECK}
        </span>
      )}
      <span>
        {label}
        {summary && <span className="text-fainter"> · {summary}</span>}
      </span>
    </p>
  );
}

/**
 * The budget, as reassurance rather than a meter (R6): a thin track with an
 * amber fill at the ratio consumed, no number and no percentage. Shown only
 * when the server says to — the caller gates on `show_bar`.
 */
export function BudgetBar({ ratio }: { ratio: number }) {
  const pct = Math.max(0, Math.min(1, Number.isFinite(ratio) ? ratio : 0)) * 100;
  return (
    <div className="h-0.5 w-full overflow-hidden bg-raised" aria-hidden="true">
      <div className="h-full bg-accent/80" style={{ width: `${pct}%` }} />
    </div>
  );
}
